import React, { type MouseEventHandler } from 'react';

// ** MUI
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import { type SxProps, type Theme, styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// ** Components
import FaIconUvilo from './FaIconUvilo';
import Spinner from './Spinner';

// ** Utilities
import { getUserName, getUserInitials, getUserAvatarId } from '@erikdakoda/auth/shared/utilities';
import type { AuthUser } from '@erikdakoda/auth/types';
import clsx from 'clsx';
import mergeSxProps from '../shared/mergeSxProps';
import CloudImage from './CloudImage';
import Tooltip from '@mui/material/Tooltip';

export const StyledAvatar = styled(Avatar)(() => ({
  '&.MuiAvatar-root': {
    '&.clickable': {
      cursor: 'pointer',
    },
    '&.clickable:hover': {
      opacity: 0.9,
    },
    '&.icon': {
      width: 24,
      height: 24,
    },

    '&.small': {
      width: 32,
      height: 32,
    },

    '&.regular': {
      width: 40,
      height: 40,
    },

    '&.large': {
      width: 48,
      height: 48,
    },

    '&.x-large': {
      width: 56,
      height: 56,
    },

    '&.xx-large': {
      width: 72,
      height: 72,
    },

    '&.profile': {
      width: 120,
      height: 120,
    },
  },
}));

export type UserForAvatar = Pick<
  AuthUser,
  'id' | 'email' | 'givenName' | 'familyName' | 'nickName' | 'role' | 'image' | 'disableAdmin'
>;

export type UserAvatarProps = {
  user: UserForAvatar | null;
  title?: string;
  isLoading?: boolean;
  size?: 'icon' | 'small' | 'regular' | 'large' | 'x-large' | 'xx-large' | 'profile';
  className?: string;
  onClick?: MouseEventHandler;
  dropShadow?: boolean;
  sx?: SxProps<Theme>;
};

export default function UserAvatar({
  user,
  title,
  isLoading,
  size = 'regular',
  className,
  onClick,
  dropShadow,
  sx,
}: UserAvatarProps) {
  'use memo';

  const theme = useTheme();
  const avatarId = getUserAvatarId(user);
  const userName = getUserName(user);
  const userInitials = getUserInitials(user);
  const badgeContent = user?.role === 'ADMIN' ? 'A' : user?.role === 'TEAM' ? 'T' : null;
  const signInIcon = <FaIconUvilo icon="right-to-bracket" />;

  const textColor = theme.palette.getContrastText(theme.palette.primary.light);

  return (
    <Box
      sx={mergeSxProps(
        dropShadow ? { filter: `drop-shadow(7px 7px 10px rgba(0, 0, 0, 0.5))` } : undefined,
        sx,
      )}
      className={clsx('user-avatar', className)}
    >
      <Badge
        className={clsx('user-avatar', className)}
        invisible={Boolean(!badgeContent)}
        sx={{
          '& .MuiBadge-badge': {
            color: 'text.primary',
            backgroundColor: user?.disableAdmin ? 'grey.500' : 'primary.main',
          },
        }}
        overlap="circular"
        onClick={onClick}
        badgeContent={badgeContent}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Tooltip title={title} enterDelay={0}>
          <StyledAvatar
            alt={userName}
            className={clsx('avatar', size, onClick ? 'clickable' : 'not-clickable')}
            data-email={user?.email}
            sx={{ bgcolor: isLoading ? 'transparent' : 'primary.light', color: textColor }}
          >
            {isLoading ? (
              <Spinner color="primary" />
            ) : !user ? (
              signInIcon
            ) : avatarId ? (
              <CloudImage cloudId={avatarId} />
            ) : userInitials ? (
              userInitials
            ) : null}
          </StyledAvatar>
        </Tooltip>
      </Badge>
    </Box>
  );
}
